import React, { useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../contexts/AuthContext';
import { ScrollArea } from './ui/scroll-area';
import { cn } from '../lib/utils';
import { MENU_ITEMS, shouldShowMenuItem } from '../constants/icons';

// Simplified nav items for the new design (Material Symbols)
export const NAV_MAIN = [
  { id: 'analysis', label: 'Analysis', labelKo: '분석', path: '/app', icon: 'analytics' },
  { id: 'records', label: 'Reports', labelKo: '리포트', path: '/reports', icon: 'description', permission: 'access:my_reports' },
];

const NAV_BOTTOM = [
  { id: 'settings', label: 'Settings', labelKo: '설정', path: '/settings', icon: 'settings' },
];

/**
 * Sidebar - Main navigation sidebar component
 * VeriPass Design System — April 2026
 */
export function Sidebar() {
  const { user, logout, hasPermission, isSiteAdmin } = useContext(AuthContext);
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const currentPath = location.pathname;
  const isKo = i18n.language === 'ko';

  const isActive = (path) => {
    if (path === '/app') return currentPath === '/app';
    return currentPath.startsWith(path);
  };

  const canShow = (item) => {
    if (!item.permission) return true;
    return hasPermission(item.permission);
  };

  const renderNavItem = (item) => {
    if (!canShow(item)) return null;
    const active = isActive(item.path);
    return (
      <Link
        key={item.id}
        to={item.path}
        className={cn(
          'flex items-center gap-[10px] py-[10px] px-4 text-sm font-medium transition-all',
          active
            ? 'text-vp-text font-bold border-l-[3px] border-vp-purple rounded-r-lg'
            : 'text-[#94a3b8] hover:text-vp-text hover:bg-white/5 ml-[3px]'
        )}
        style={active ? { backgroundColor: 'rgba(161,0,170,0.12)' } : undefined}
      >
        <span className="material-symbols-outlined" style={{ fontSize: 20 }}>
          {item.icon}
        </span>
        <span>{isKo ? item.labelKo : item.label}</span>
      </Link>
    );
  };

  // Check if user has any admin permissions — show admin section if so
  const hasAdminAccess = MENU_ITEMS.admin.some(item => shouldShowMenuItem(item, hasPermission, isSiteAdmin));

  const initials = user?.full_name
    ? user.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : user?.email?.[0]?.toUpperCase() || '?';

  return (
    <div
      className="flex h-screen flex-col"
      style={{
        width: 220,
        backgroundColor: 'var(--shell-sidebar-bg)',
        borderRight: '1px solid var(--shell-sidebar-border)',
        color: 'var(--shell-sidebar-text)',
        paddingTop: 56, // below TopNav
      }}
    >
      {/* Main nav */}
      <ScrollArea className="flex-1 py-3">
        <nav className="space-y-1">
          {NAV_MAIN.map(renderNavItem)}
        </nav>

        {/* Admin section — only show if user has admin perms */}
        {hasAdminAccess && (
          <>
            <div className="mx-4 my-2" style={{ borderTop: '1px solid var(--shell-sidebar-border)' }} />
            {MENU_ITEMS.admin.map((item) => {
              if (!shouldShowMenuItem(item, hasPermission, isSiteAdmin)) return null;
              if (item.id === 'profiles' && !user?.can_manage_admins) return null;
              const active = currentPath.startsWith(item.path);
              return (
                <Link
                  key={item.id}
                  to={item.path}
                  className={cn(
                    'flex items-center gap-[10px] py-[10px] px-4 text-sm font-medium transition-all',
                    active
                      ? 'text-vp-text font-bold border-l-[3px] border-vp-purple rounded-r-lg'
                      : 'text-[#94a3b8] hover:text-vp-text hover:bg-white/5 ml-[3px]'
                  )}
                  style={active ? { backgroundColor: 'rgba(161,0,170,0.12)' } : undefined}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 20 }}>
                    {item.id === 'test-runner' ? 'science' :
                     item.id === 'warning-letters' ? 'warning' :
                     item.id === 'data-explorer' ? 'storage' :
                     item.id === 'site-users' ? 'admin_panel_settings' :
                     item.id === 'users' ? 'group' :
                     item.id === 'manual-payments' ? 'payments' :
                     item.id === 'audit-log' ? 'shield' :
                     item.id === 'sessions' ? 'devices' :
                     item.id === 'pipeline-health' ? 'monitor_heart' :
                     item.id === 'email-manager' ? 'mail' :
                     item.id === 'profiles' ? 'manage_accounts' :
                     item.id === 'marketing-ai' ? 'auto_awesome' :
                     item.id === 'promotions' ? 'local_activity' :
                     'extension'}
                  </span>
                  <span className="flex-1 truncate">{item.label}</span>
                  {item.badge && (
                    <span className="text-[10px] font-bold text-vp-subtext bg-white/5 px-1.5 py-0.5 rounded">
                      {item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </>
        )}

        {/* Divider before Settings */}
        <div className="mx-4 my-2" style={{ borderTop: '1px solid var(--shell-sidebar-border)' }} />
        <nav className="space-y-1">
          {NAV_BOTTOM.map(renderNavItem)}
        </nav>
      </ScrollArea>

      {/* User profile section */}
      <div style={{ borderTop: '1px solid var(--shell-sidebar-border)', padding: 16 }}>
        <div className="flex items-center gap-3">
          <div
            className="flex items-center justify-center font-headline font-extrabold text-white"
            style={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              backgroundColor: '#a100aa',
              fontSize: 13,
            }}
          >
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <p className="truncate text-sm font-medium text-vp-text">
              {user?.full_name || user?.email || 'Guest'}
            </p>
            <p className="text-[11px] text-vp-subtext truncate">
              {user?.email || ''}
            </p>
          </div>
        </div>
        <button
          onClick={logout}
          className="mt-3 w-full text-center py-2 text-xs font-medium rounded-lg transition-all"
          style={{
            border: '1px solid var(--shell-sidebar-border)',
            background: 'transparent',
            color: 'var(--shell-sidebar-muted)',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#a100aa'; e.currentTarget.style.color = 'var(--shell-sidebar-text)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--shell-sidebar-border)'; e.currentTarget.style.color = 'var(--shell-sidebar-muted)'; }}
        >
          {t('navigation.logout')}
        </button>
      </div>
    </div>
  );
}

export default Sidebar;
